import { useState } from "react"
import { LoginModal } from "../../components/LoginModal.js"
import { trackEvent } from "../../lib/analytics.js"
import { useAuth } from "../../lib/auth.js"
import { usePopover } from "../../lib/usePopover.js"
import styles from "./FavoriteButton.module.css"
import { useLists } from "./useLists.js"

interface Props {
  companyId: string
  popoverPlacement?: "above" | "below"
}

export function FavoriteButton({ companyId, popoverPlacement = "below" }: Props) {
  const { isLoggedIn } = useAuth()
  const { lists, quickAddToLastUsed, addItem, removeItem, createList } = useLists()
  const { open, setOpen, rootRef } = usePopover<HTMLDivElement>()
  const [pending, setPending] = useState(false)
  const [justAddedListName, setJustAddedListName] = useState<string | null>(null)
  const [newListName, setNewListName] = useState("")
  const [loginModalOpen, setLoginModalOpen] = useState(false)

  const memberLists = lists.filter((l) => l.items.some((i) => i.companyId === companyId))
  const isFavorited = memberLists.length > 0

  async function handleStarClick() {
    if (!isLoggedIn) {
      trackEvent("favorite_click_logged_out")
      setLoginModalOpen(true)
      return
    }
    setPending(true)
    try {
      const { list } = await quickAddToLastUsed(companyId)
      setJustAddedListName(list.name)
      setOpen(true)
    } finally {
      setPending(false)
    }
  }

  async function handleToggleList(listId: string, checked: boolean) {
    if (checked) {
      await addItem(listId, companyId)
    } else {
      const list = lists.find((l) => l.id === listId)
      const item = list?.items.find((i) => i.companyId === companyId)
      if (!item) return
      try {
        await removeItem(listId, item.id)
      } catch (e) {
        console.error(e)
        window.alert("リストからの削除に失敗しました。時間をおいて再度お試しください。")
      }
    }
  }

  async function handleCreateList(e: React.FormEvent) {
    e.preventDefault()
    const name = newListName.trim()
    if (!name) return
    const list = await createList(name)
    await addItem(list.id, companyId)
    setNewListName("")
    setJustAddedListName(list.name)
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={`${styles.star} ${isFavorited ? styles.starActive : ""}`}
        aria-label={isFavorited ? "お気に入りに登録済み" : "お気に入りに登録"}
        aria-pressed={isFavorited}
        disabled={pending}
        onClick={handleStarClick}
      >
        {isFavorited ? "★" : "☆"}
      </button>

      {open && isLoggedIn && (
        <div
          className={`${styles.popover} ${popoverPlacement === "above" ? styles.popoverAbove : ""}`}
          role="menu"
        >
          {justAddedListName && (
            <p className={styles.toast}>「{justAddedListName}」に追加しました</p>
          )}
          <ul className={styles.listOptions}>
            {lists.map((list) => {
              const checked = list.items.some((i) => i.companyId === companyId)
              return (
                <li key={list.id}>
                  <label className={styles.listOption}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => handleToggleList(list.id, e.target.checked)}
                    />
                    {list.name}
                  </label>
                </li>
              )
            })}
          </ul>
          <form className={styles.createForm} onSubmit={handleCreateList}>
            <input
              type="text"
              className={styles.createInput}
              placeholder="＋ 新しいリスト"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
            />
          </form>
        </div>
      )}

      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        returnTo={`${window.location.pathname}${window.location.search}`}
      />
    </div>
  )
}
